import { type IMergedSlot, PU_EVENT_ORDER, type PuEventType } from '../interface/pu-events';

// Pure schedule logic for the Election Day Pulse nudge engine — no React.
// Times follow the standard INEC election-day timetable (WAT). Events with a
// null expectedHHMM are sequence-derived: their window opens once the
// previous milestone is captured.

export interface IEventWindow {
	eventType: PuEventType;
	/** Wall-clock expected time ("HH:MM", device-local). null = sequence-derived. */
	expectedHHMM: string | null;
	/** Minutes after the window opens before the event counts as overdue. */
	overdueAfterMinutes: number;
}

export interface IElectionDaySchedule {
	windows: IEventWindow[];
}

export const DEFAULT_INEC_SCHEDULE: IElectionDaySchedule = {
	windows: [
		{ eventType: 1, expectedHHMM: '07:30', overdueAfterMinutes: 45 }, // OfficialArrived
		{ eventType: 2, expectedHHMM: '08:00', overdueAfterMinutes: 45 }, // MaterialsArrived
		{ eventType: 3, expectedHHMM: '08:30', overdueAfterMinutes: 45 }, // AccreditationCommenced
		{ eventType: 4, expectedHHMM: '08:30', overdueAfterMinutes: 90 }, // VotingCommenced
		{ eventType: 5, expectedHHMM: '14:30', overdueAfterMinutes: 90 }, // VotingStopped (vote-while-in-queue)
		{ eventType: 6, expectedHHMM: null, overdueAfterMinutes: 45 }, // CountingCommenced
		{ eventType: 7, expectedHHMM: null, overdueAfterMinutes: 120 }, // CountingStopped
		{ eventType: 8, expectedHHMM: null, overdueAfterMinutes: 120 }, // ResultsAnnounced
	],
};

const minutesOfDay = (date: Date) => date.getHours() * 60 + date.getMinutes();

const parseHHMM = (hhmm: string): number => {
	const [h, m] = hhmm.split(':').map(Number);
	return h * 60 + (m || 0);
};

export const getWindow = (schedule: IElectionDaySchedule, eventType: PuEventType): IEventWindow | undefined =>
	schedule.windows.find((w) => w.eventType === eventType);

/**
 * Whether an event's capture window has opened: its wall-clock time has
 * passed, or (sequence-derived) every earlier event is already captured.
 */
export const isWindowOpen = (
	eventType: PuEventType,
	captured: Set<PuEventType>,
	schedule: IElectionDaySchedule,
	now: Date
): boolean => {
	const window = getWindow(schedule, eventType);
	if (window?.expectedHHMM) {
		return minutesOfDay(now) >= parseHHMM(window.expectedHHMM);
	}
	const index = PU_EVENT_ORDER.indexOf(eventType);
	return PU_EVENT_ORDER.slice(0, index).every((earlier) => captured.has(earlier));
};

/**
 * The event the agent should capture next: the lowest-numbered uncaptured
 * event. (The lifecycle is strictly ordered for guidance; out-of-order
 * capture is still allowed from the full panel — reality is messy.)
 */
export const deriveNextExpected = (captured: Set<PuEventType>): PuEventType | null => {
	for (const eventType of PU_EVENT_ORDER) {
		if (!captured.has(eventType)) return eventType;
	}
	return null;
};

/**
 * Uncaptured events whose window opened more than `overdueAfterMinutes` ago.
 * Sequence-derived events count from "all earlier events captured" and use
 * the latest earlier capture as their anchor when available, else they are
 * not overdue (we can't know counting should have started without a clock).
 */
export const deriveOverdue = (
	captured: Set<PuEventType>,
	schedule: IElectionDaySchedule,
	now: Date,
	capturedAt?: Partial<Record<PuEventType, string>>
): PuEventType[] => {
	const overdue: PuEventType[] = [];
	for (const window of schedule.windows) {
		if (captured.has(window.eventType)) continue;
		if (window.expectedHHMM) {
			const openedAt = parseHHMM(window.expectedHHMM);
			if (minutesOfDay(now) - openedAt >= window.overdueAfterMinutes) {
				overdue.push(window.eventType);
			}
			continue;
		}
		// Sequence-derived: anchor on the most recent earlier capture timestamp.
		const index = PU_EVENT_ORDER.indexOf(window.eventType);
		const earlier = PU_EVENT_ORDER.slice(0, index);
		if (!earlier.every((e) => captured.has(e))) continue;
		const anchors = earlier
			.map((e) => capturedAt?.[e])
			.filter((iso): iso is string => !!iso)
			.map((iso) => new Date(iso).getTime())
			.filter((t) => Number.isFinite(t));
		if (anchors.length === 0) continue;
		const elapsedMinutes = (now.getTime() - Math.max(...anchors)) / 60_000;
		if (elapsedMinutes >= window.overdueAfterMinutes) {
			overdue.push(window.eventType);
		}
	}
	return overdue;
};

/**
 * Nudges only make sense while an election day is actually in progress.
 * Heuristic: at least one event window is open or any event was already
 * captured today, and the timeline is not fully complete. (No backend
 * election-date field exists yet — when one ships, gate on it here.)
 */
export const isNudgingActive = (captured: Set<PuEventType>, schedule: IElectionDaySchedule, now: Date): boolean => {
	if (captured.size >= PU_EVENT_ORDER.length) return false;
	if (captured.size > 0) return true;
	const first = schedule.windows.find((w) => w.expectedHHMM);
	if (!first?.expectedHHMM) return false;
	return minutesOfDay(now) >= parseHHMM(first.expectedHHMM);
};

/** Helper for the UI: the captured set + timestamps from merged slots. */
export const toCapturedSet = (slots: IMergedSlot[]): Set<PuEventType> =>
	new Set(slots.filter((s) => s.status === 'captured' || s.status === 'syncing').map((s) => s.eventType));

export const toCapturedAtMap = (slots: IMergedSlot[]): Partial<Record<PuEventType, string>> => {
	const map: Partial<Record<PuEventType, string>> = {};
	slots.forEach((s) => {
		if ((s.status === 'captured' || s.status === 'syncing') && s.occurredAt) {
			map[s.eventType] = s.occurredAt;
		}
	});
	return map;
};
